import { _ } from "../../core/polyglot/polyglot.js";
import { makeMonitorRepository } from "../../core/repositories/repositoryBuilders.js";
import ChartOfAccounts from "../../models/chartOfAccounts/ChartOfAccounts.js";
import getKeyFigureService from "../../core/services/getKeyFigureService.js";
import InternationalizationService from "../../core/services/InternationalizationService.js";
import InterpretationContext from "../../models/reports/InterpretationContext.js";
import notifier from "../../core/widgets/notifier/notifier.js";
import ReportDataService from "./ReportDataService.js";
import ReportTemplate from "../../models/reports/templates/ReportTemplate.js";
import session from "../../core/session.js";
import TemplateInterpreter from "../../models/reports/TemplateInterpreter.js";

/**
 * ReportService encapsulates fetching report templates and its data
 * dependencies, as well as saving and removing report templates.
 *
 * This service also handles report template interpretation and report data
 * fetching.
 *
 * Key figures must already be in `getKeyFigureService` cache before using
 * `fetchChartOfAccountsAndKeyFigures()`. They are generally fetched with Accounting
 * documents.
 *
 * @param {period} period - Period used for report
 *   interpretation.
 * @param {ReportTemplate} spec.reportTemplate - Report template used for report
 *   interpretation. Used when the report template is created on the client and
 *   not supposed to be fetched from the server.
 * @param {Company} company -
 * @param {ChartOfAccounts} [chartOfAccounts] - The chart of accounts is needed for
 *   the InterpretationContext. This argument is optional because it can be fetched
 *   using chartOfAccountsId.
 * @param {string} [chartOfAccountsId] - The chart of accounts id to fetch in case
 *   the chartOfAccounts is not provided. This argument is optional because it can be
 *   specified by the chartOfAccounts parameter.
 * @param {KeyFigure[]} [keyFigures] - The list of keyfigures is needed for
 *   the InterpretationContext. This argument is optional because it can be fetched.
 */
export default class ReportService {
	constructor({
		period,
		reportTemplate,
		company,
		chartOfAccounts = null,
		chartOfAccountsId = chartOfAccounts?.getId(),
		keyFigures = null,
	} = {}) {
		this._reportTemplate = reportTemplate || new ReportTemplate();
		this._intepretationContext = null;

		this._period = period;
		this._company = company;
		this._chartOfAccounts = chartOfAccounts;
		this._keyFigures = keyFigures;

		const antiForgeryToken = session.getAntiForgeryToken();

		this._templateRepository = makeMonitorRepository({
			route: "/Shared/ReportTemplate/Company/#companyId/?id",
			model: ReportTemplate,
			defaultFilters: {
				companyId: this._company.getLegacyId(),
			},
			antiForgeryToken,
		});

		this._chartOfAccountsRepository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/ChartOfAccounts/#id",
			model: ChartOfAccounts,
			defaultFilters: {
				companyId: this._company.getLegacyId(),
				id: chartOfAccountsId,
			},
			antiForgeryToken,
		});

		this._keyFigureService = getKeyFigureService();
	}

	setReportTemplate(reportTemplate) {
		this._reportTemplate = reportTemplate;
		this._report = null;
	}

	resetReportTemplate() {
		this.setReportTemplate(new ReportTemplate());
	}

	getReport() {
		return this._report;
	}

	getReportTemplate() {
		return this._reportTemplate;
	}

	getInterpretationContext() {
		return this._intepretationContext;
	}

	getChartOfAccounts() {
		return this._chartOfAccounts;
	}

	setPeriod(period) {
		this._period = period;
	}

	getPeriod() {
		return this._period;
	}

	async fetchReportTemplate(reportTemplateId) {
		const reportTemplate = await this._templateRepository.get({
			filter: { id: reportTemplateId },
		});

		this._reportTemplate = reportTemplate;
		return reportTemplate;
	}

	async fetchChartOfAccountsAndKeyFigures() {
		const [chartOfAccounts, keyFigures] = await Promise.all([
			this._chartOfAccountsRepository.get(),
			this._keyFigureService.getAllFromCache(),
		]);

		this._chartOfAccounts = chartOfAccounts;
		this._keyFigures = keyFigures;

		return this._ensureChartOfAccountsLanguage();
	}

	/**
	 * Save a `reportTemplate` and evaluate `callback`.
	 *
	 * If the report template is invalid, no save is performed, a notification
	 * is displayed instead.
	 *
	 * If the report template is already in the database, perform an update.
	 */
	saveReportTemplate({ reportTemplate, callback = () => {} }) {
		let saveMethod;

		if (!reportTemplate.isValid()) {
			notifier.error(_("Invalid template, please correct errors first."));

			return;
		}

		if (reportTemplate.isNewTemplate()) {
			saveMethod = this._templateRepository.save.bind(
				this._templateRepository,
			);
		} else {
			saveMethod = this._templateRepository.update.bind(
				this._templateRepository,
			);
		}

		saveMethod({
			model: reportTemplate,
		}).then((newReportTemplate) => {
			this._reportTemplate = newReportTemplate;
			reportTemplate.beSaved();
			callback();
		});
	}

	removeReportTemplate({ reportTemplate, callback }) {
		this._templateRepository
			.remove({
				model: reportTemplate,
				filter: {
					id: reportTemplate.getId(),
				},
			})
			.then(callback);
	}

	updateReportAndData({
		repeatingPeriods,
		dimensionSpecification,
		itemSettings,
		throttle = false,
	} = {}) {
		this.updateReport({
			repeatingPeriods,
			dimensionSpecification,
			itemSettings,
		});

		if (this._report.isValid()) {
			return this.updateReportData({ throttle });
		}

		return Promise.resolve();
	}

	updateReport({
		repeatingPeriods,
		dimensionSpecification,
		itemSettings,
		keepExpansion,
	} = {}) {
		this._intepretationContext = this._makeInterpretationContext({
			repeatingPeriods,
			dimensionSpecification,
			itemSettings,
		});

		const interpreter = new TemplateInterpreter({
			context: this._intepretationContext,
		});

		const report = interpreter.interpret(this._reportTemplate);
		if (keepExpansion) report.expandAccordingTo(this._report);

		this._report = report;
	}

	/**
	 * Update the data of the `_report` instance variable.
	 *
	 * Important note: A report must have been previously fetched (either using
	 * `updateReport` or `updateReportAndData`).
	 *
	 * if `throttle` is truthy, the data request is throttled (See
	 * `reportDataService` for more information about the request throttling).
	 */
	async updateReportData({ throttle }) {
		const data =
			await ReportDataService.instance().fetchCalculatedReportData({
				throttle,
				context: this._intepretationContext,
				report: this._report,
			});

		this._report.setData({ data });
		return this._report;
	}

	_makeInterpretationContext({
		repeatingPeriods = [],
		dimensionSpecification,
		itemSettings,
	}) {
		return new InterpretationContext({
			reportTemplate: this._reportTemplate,
			chartOfAccounts: this._chartOfAccounts,
			company: this._company,
			dimensionSpecification,
			keyFigures: this._keyFigures,
			period: this._period,
			repeatingPeriods,
			templateSettings: itemSettings,
		});
	}

	_ensureChartOfAccountsLanguage() {
		const chartOfAccountsLanguage =
			this._chartOfAccounts && this._chartOfAccounts.getLanguage();

		if (!chartOfAccountsLanguage) {
			return Promise.resolve();
		}

		return InternationalizationService.instance().ensureTranslations(
			chartOfAccountsLanguage,
		);
	}
}
